/** 리디렉션 관련 유틸 함수
 *  - 허용 도메인 화이트리스트 기반 리디렉션 제어
 *  - 로컬 개발시 '로컬 호스트로의 리디렉션' 허용 여부 포함
 */
let redirect = {};

import axios from 'axios';

// 리디렉션 허용 도메인 목록
import { ALLOWED_HOSTS as FILE_ALLOWED_HOSTS } from '../../config/allowed-hosts.js';
const ALLOWED_HOST_LIST = (Array.isArray(FILE_ALLOWED_HOSTS) ? FILE_ALLOWED_HOSTS : [])
  .map(h => String(h).trim().toLowerCase()) // 문자열 변환, 공백 제거, 소문자로 치환
  .filter(Boolean); // 빈 값 제거
const ALLOWED_HOST_SET = new Set(ALLOWED_HOST_LIST);

// 개발 환경 여부 | true: 개발환경, false: 배포환경
const IS_DEV = process.env.VUE_APP_IS_DEV === 'true';
// 로그아웃/미로그인시 고정 이동 사이트
const REDIRECT_DEFAULT_SITE_URL = process.env.VUE_APP_REDIRECT_DEFAULT_SITE_URL;
// 로컬 개발시 고정 이동 사이트
const DEV_REDIRECT_DEFAULT_SITE_URL = process.env.VUE_APP_DEV_REDIRECT_DEFAULT_SITE_URL;
// 로컬 개발시 '로컬 호스트로의 리디렉션' 허용 여부 | true: 리디렉션 허용, false: 리디렉션 비허용
const LOCALHOST_REDIRECT_ENABLED = process.env.VUE_APP_LOCALHOST_REDIRECT_ENABLED === 'true';

/** URL 파싱(실패 시 null) */
function parseUrl(u) {
  try {
    // 잘못된 URL 방어
    if(typeof u !== 'string') return null;
    // 상대경로 방어
    if(u.startsWith('/')) {
      if (typeof window === 'undefined') return null; // SSR/테스트 방어
      return new URL(u, window.location.origin);
    }
    return new URL(u);
  } catch { return null; }
}

/** 개발용 로컬 리디렉션 URL 여부 */
function isDevHost(url) {
  const u = parseUrl(url);
  // 파싱 실패
  if(!u) return false;
  const host = u.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

/** 리디렉션 허용 도메인 여부
 * @param {string} url 검사할 URL
 * @returns {boolean} 허용 도메인 여부
 */
function isAllowedHost(url) {
  const u = parseUrl(url);
  if (!u) return false;
  // 개발 환경에서 로컬리디렉션 허용 옵션이 활성화되고 로컬호스트 URL인 경우 허용
  if (IS_DEV && LOCALHOST_REDIRECT_ENABLED && isDevHost(url)) return true;
  // 화이트리스트 기반 허용 도메인 여부 판단
  return ALLOWED_HOST_SET.has(u.hostname);
}

/** 정규화 URL(string)
 * @param {string} url 정규화할 URL
 * @returns {object|null} { origin, pathname } | null: 파싱 실패
 */
function normalize(url) {
  const u = parseUrl(url);
  if(!u) return null;
  // 트레일링 슬래시 정규화
  const pathname = u.pathname;
  const path = pathname && pathname.endsWith('/') && pathname !== '/' 
    ? pathname.slice(0, -1) 
    : pathname;
  return { origin: u.origin, pathname: path || '/' };
}

/** 리디렉션 필요 여부 판단
 * @param {string} serverUrl 현재 접속 서버 URL (예: http://youngil.cafe24app.com)
 * @param {string} targetUrl 리디렉션 목표 URL (예: http://youngilwest.cafe24app.com)
 * @returns {boolean} 리디렉션 필요 여부
 * - 도메인 화이트리스트에 없는 타겟은 차단
 * - 같은 origin + 같은 경로면 이동 X
 * - 로컬 우회 옵션 적용인 경우 리디렉션 X
 */
export function shouldRedirect(serverUrl, targetUrl) {
  if(!serverUrl || !targetUrl) return false;

  // 리디렉션 허용 여부 체크
  if(!isAllowedHost(targetUrl)) return false;

  const normalizedServerUrl = normalize(serverUrl);
  const normalizedTargetUrl = normalize(targetUrl);
  if(!normalizedServerUrl || !normalizedTargetUrl) return false;

  // 같은 origin + 같은 경로면 이동 불필요
  if(normalizedServerUrl.origin === normalizedTargetUrl.origin && normalizedServerUrl.pathname === normalizedTargetUrl.pathname) return false;

  return true;
}

/** 리디렉션 URL 구성 (로그인 정보 포함) */
function buildRedirectUrl(targetUrl, loginInfo) {
  if (!targetUrl) return '';
  if (!loginInfo) return targetUrl;
  
  const url = parseUrl(targetUrl);
  if (!url) return targetUrl;
  
  // 기존 쿼리 파라미터 유지하면서 logininfo 추가
  url.searchParams.set('logininfo', loginInfo);
  return url.toString();
}


/** 환경별 기본 사이트 URL 결정
 * - build(운영) -> 기본 사이트
 * - 개발 환경 -> 로컬 리디렉션 허용 -> 개발용 기본 사이트
 * - 개발 환경 -> 리디렉션 비허용 -> null
 */
function getTargetSiteUrl() {
  if (!IS_DEV) {
    return REDIRECT_DEFAULT_SITE_URL;
  } else if (LOCALHOST_REDIRECT_ENABLED) {
    return DEV_REDIRECT_DEFAULT_SITE_URL;
  }
  return null;
}

/** 현재 사이트에서 타겟 사이트로의 리디렉션 처리
 * @param {string} targetUrl 목표 사이트 URL
 * @param {object} options 옵션 객체
 * @param {boolean} options.addLogoutFlag 로그아웃 완료 플래그 추가 여부
 * @param {boolean} options.reloadIfSameSite 같은 사이트일 때도 새로고침 실행 여부
 */
function handleSiteRedirect(targetUrl, options = {}) {
  const { addLogoutFlag = false, reloadIfSameSite = false } = options;
  
  // SSR/테스트 환경 방어
  if (typeof window === 'undefined') return;
  
  // 현재 URL
  const currentUrl = window.location.origin + window.location.pathname;
  
  // 타겟 URL이 없는 경우 (개발 환경에서 리디렉션 비허용 시)
  if (!targetUrl) {
    // 새로고침 옵션이 활성화된 경우 새로고침
    if (reloadIfSameSite) window.location.reload();
    return;
  }
  
  // 현재 사이트와 타겟 사이트가 같은 경우
  if (!shouldRedirect(currentUrl, targetUrl)) {
    // 새로고침 옵션이 활성화된 경우 새로고침
    if (reloadIfSameSite) window.location.reload();
    return;
  }
  
  // 타겟 URL 파싱
  const url = parseUrl(targetUrl);
  if (!url) return;
  
  // 로그아웃 플래그있는 경우 파라미터 추가
  if (addLogoutFlag) {
    url.searchParams.set('logout', 'complete');
  }
  
  // 다른 사이트로 리디렉션
  redirectTo(url.toString());
}

/** 환경별 기본 사이트로 이동 | to: REDIRECT_DEFAULT_SITE_URL or DEV_REDIRECT_DEFAULT_SITE_URL
 * - build(운영)에서는 무조건 기본 사이트로 이동
 * - 개발 환경: 로컬 리디렉션 허용 시 로컬 개발용 기본 사이트로 이동
 * - 리디렉션 비허용 시 작동 X
 * - 현재 사이트가 이미 기본 사이트인 경우 리디렉션하지 않음 (무한 리디렉션 방지)
 */
export function redirectToHomeSite() {
  const targetUrl = getTargetSiteUrl();
  handleSiteRedirect(targetUrl);
}

/** 서버 로그인 상태 확인 */
async function authCheck() {
  try {
    const res = await axios.get('/api/auth/check', {
      validateStatus: (status) => status < 500,
    });
    return res?.data || {};
  } catch (err) {
    console.error('[authCheck] /api/auth/check 실패:', err);
    return null;
  }
}

/** ----------------------------------여기부터 외부 노출 함수---------------------------------- */

/** URL 이동 */
export function redirectTo(url) {
  if(typeof window === 'undefined') return; // SSR/테스트 방어
  const u = parseUrl(url);
  if(!u) return;
  if(!isAllowedHost(url)) return;
  window.location.replace(u.toString());
}
redirect.redirectTo = redirectTo;

/** 리디렉션 처리 헬퍼 | 사이트 접속 시 리디렉션 여부 판단 및 처리
 * @param {object} $route Vue 라우터 객체
 * @param {object} $store Vuex 스토어 객체
 * - 쿼리 파라미터에 암호화된 로그인 정보가 있으면 우선 처리
 * - 일반회원이면 소속 사이트로 암호화된 로그인 정보와 함께 리디렉션
 * - 비로그인은 지정된 사이트( REDIRECT_DEFAULT_SITE_URL | DEV_REDIRECT_DEFAULT_SITE_URL )로 리디렉션
 * - 관리자는 별도 리디렉션 없음 (아무것도 하지 않음)
 */
export async function handleMountedRedirect($route, $store) {
  // JWT 기반 인증에 최적화된 마운트 시 리디렉션 처리
  // - 비로그인시에 운영 -> REDIRECT_DEFAULT_SITE_URL, 개발 -> 리디렉션 허용 옵션에 따라 DEV_REDIRECT_DEFAULT_SITE_URL 로 리디렉션
  // - 관리자는 별도 리다이렉션 없음
  // - 로그인시 일반회원이면 소속 사이트로 리디렉션
  // - 리디렉션이 필요한경우 암호화된 로그인 정보도 함께 전달
  // - 암호화된 로그인 정보가 쿼리에 있으면 우선 처리후, 정상 로그인 이후 암호화된 로그인 정보 삭제

  // 0) 로그아웃 완료 플래그 확인 - JWT 토큰 만료 및 쿠키 삭제 완료된 상태
  const isLogoutComplete = $route?.query?.logout === 'complete';
  if (isLogoutComplete) {
    // 로그아웃 완료 플래그가 있으면 클라이언트 상태만 정리
    // JWT는 서버에 세션이 없으므로 추가 API 호출 불필요
    $store.dispatch('clearClientState');
    
    // URL에서 로그아웃 플래그 제거
    const currentUrl = window.location.origin + window.location.pathname;
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', currentUrl);
    }
    return;
  }

  // 1) 쿼리 암호화 로그인 우선 처리 (있으면 처리 후 종료)
  const encryptedLoginInfo = $route?.query?.logininfo;
  if (encryptedLoginInfo) {
    await $store.dispatch('encryptedLogin', encryptedLoginInfo);
    return;
  }

  // 2) 서버 로그인 상태 확인 (JWT 토큰 유효성 검증)
  const data = await authCheck();
  if (!data) return;
  const { isLogin, type, serverUrl, targetUrl, loginInfo, redirectEnabled } = data;

  // 3) 일반회원(type 1) 접속 시 소속 사이트로 조건부 리다이렉트
  if (isLogin && type === 1) {
    // 리디렉션 필요 시 이동
    if (shouldRedirect(serverUrl, targetUrl)) {
      window.location.replace(buildRedirectUrl(targetUrl, loginInfo));
      return;
    }
    return; // 리디렉션 불필요
  }

  // 4) 비로그인 처리 (JWT 토큰 없거나 만료됨)
  if (!isLogin) {
    // 클라이언트 상태와 JWT 상태가 다를 수 있으므로 동기화
    if ($store.state.isLogin) {
      $store.dispatch('clearClientState');
    }
    if (redirectEnabled) redirectToHomeSite();
    return;
  }

  // 5) 관리자(type 0)는 별도 리디렉션 없음 (아무것도 하지 않음)
}
redirect.handleMountedRedirect = handleMountedRedirect;

/** 로그인 후 리디렉션 처리 (일반 로그인 & 암호화 로그인 공통) */
export async function handleLoginRedirect() {
  // 현재 경로
  const currentUrl = window.location.origin + window.location.pathname;
  
  try {
    // 서버 로그인 상태 확인
    const data = await authCheck();
    // 로그인 정보가 없으면 현재 URL로 리디렉션
    if (!data) {
      redirectTo(currentUrl);
      return;
    }
    
    const { serverUrl, targetUrl, loginInfo: autoLoginInfo } = data;
    
    // 리디렉션 필요한 경우
    if (shouldRedirect(serverUrl, targetUrl)) {
      redirectTo(buildRedirectUrl(targetUrl, autoLoginInfo));
      return;
    }
    
    // 리디렉션 불필요한 경우 현재 경로 유지(새로고침)
    redirectTo(currentUrl);
  } catch (e) {
    // 체크 실패 시 현재 사이트로 리프레시
    redirectTo(currentUrl);
  }
}
redirect.handleLoginRedirect = handleLoginRedirect;

/** 로그아웃 후 리디렉션 처리
 * JWT 기반 인증에 최적화된 로그아웃 처리
 * - 서버에서 refresh token 삭제 및 쿠키 만료 처리 완료 후 리디렉션
 * - 로그아웃 완료 플래그로 타겟 사이트에서 중복 처리 방지
 */
export function handleLogoutRedirect() {
  const targetUrl = getTargetSiteUrl();
  handleSiteRedirect(targetUrl, { 
    addLogoutFlag: true, 
    reloadIfSameSite: true 
  });
}
redirect.handleLogoutRedirect = handleLogoutRedirect;

export default redirect;